import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { fetchPortalData, postPortalData } from '@/api/portals'
import { PortalShell } from '@/layouts/PortalShell'
import { EmptyState, ErrorState, PageSkeleton } from '@/components/ui/States'
import { getApiErrorMessage } from '@/lib/apiClient'

export default function PortalListPage({
  title,
  apiPath,
  listKey,
  columns,
  actions,
}: {
  title: string
  apiPath: string
  listKey: string
  columns: Array<{ key: string; label: string; render?: (row: Record<string, unknown>) => string }>
  actions?: Array<{
    label: string
    path: (row: Record<string, unknown>) => string
    confirm?: string
  }>
}) {
  const qc = useQueryClient()
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const q = useQuery({
    queryKey: ['portal-list', apiPath],
    queryFn: () => fetchPortalData(apiPath),
  })

  const act = useMutation({
    mutationFn: (path: string) => postPortalData(path),
    onSuccess: (res) => {
      setMsg(res.message || 'Updated.')
      setErr(null)
      void qc.invalidateQueries({ queryKey: ['portal-list', apiPath] })
    },
    onError: (e) => {
      setErr(getApiErrorMessage(e))
      setMsg(null)
    },
  })

  if (q.isLoading) {
    return (
      <PortalShell title={title}>
        <PageSkeleton />
      </PortalShell>
    )
  }
  if (q.isError) {
    return (
      <PortalShell title={title}>
        <div className="p-6">
          <ErrorState message={getApiErrorMessage(q.error)} onRetry={() => void q.refetch()} />
        </div>
      </PortalShell>
    )
  }

  const rowsRaw = q.data?.[listKey]
  const rows = Array.isArray(rowsRaw)
    ? (rowsRaw as Array<Record<string, unknown>>)
    : Array.isArray(q.data?.items)
      ? (q.data!.items as Array<Record<string, unknown>>)
      : []

  return (
    <PortalShell title={title}>
      <div className="mx-auto max-w-6xl space-y-4 p-6">
        {msg ? <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{msg}</div> : null}
        {err ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div> : null}

        {!rows.length ? (
          <EmptyState title={`No ${title.toLowerCase()} found`} />
        ) : (
          <div className="overflow-hidden rounded-[14px] border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    {columns.map((c) => (
                      <th key={c.key} className="px-4 py-2 text-left text-xs font-semibold uppercase text-slate-500">
                        {c.label}
                      </th>
                    ))}
                    {actions?.length ? <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-slate-500">Actions</th> : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row, idx) => (
                    <tr key={String(row.id || idx)}>
                      {columns.map((c) => (
                        <td key={c.key} className="px-4 py-2.5 text-sm text-slate-800">
                          {c.render ? c.render(row) : String(deepGet(row, c.key) ?? '—')}
                        </td>
                      ))}
                      {actions?.length ? (
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-2">
                            {actions.map((a) => (
                              <button
                                key={a.label}
                                type="button"
                                disabled={act.isPending}
                                className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-bold text-[#1e5a9f]"
                                onClick={() => {
                                  if (a.confirm && !window.confirm(a.confirm)) return
                                  act.mutate(a.path(row))
                                }}
                              >
                                {a.label}
                              </button>
                            ))}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  )
}

function deepGet(obj: Record<string, unknown>, path: string) {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key]
    return undefined
  }, obj)
}
